import * as React from 'react';
import ReactPlayer from 'react-player';
import Slider from 'rc-slider';
import styled, { css } from 'styled-components';
import * as UAParser from "ua-parser-js";
import { toHHMMSS, toTime } from '../utils/helpers';
import { mode, dataLayer } from '../App';

const parser = new UAParser();
const ua = parser.getResult();

const colors = ['#d79013', '#50beb5', '#967c52', '#21819f'];
const TRACK_HEIGHT = 5;

const Wrapper = styled.div`
  position: relative;
  /* overflow: hidden; */
  transition: all 0.8s;
`;

const CuePointsToolWrapper = styled.div`
  position: relative;
  padding-bottom: 40px;
  background-color: ${props => props.theme.colors.player};
`;

const CuePointsTool = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 20px 24px;
  padding-top: 24px;
  background-color: ${props => props.theme.colors.player};

  @media only screen and (min-width: 700px) {
    padding: 8px 60px 24px;
  }
`;

const CuePointsBar = styled.div`
  position: relative;
  display: inline-block;
  width: calc(100% - 120px);
  left: -4px;

  .rc-slider-rail, .rc-slider-track {
    background-color: transparent;
  }

  &::before {
    content: ' ';
    background-color: #a3a3a3;
    display: block;
    position: absolute;
    top: 5px;
    left: 0;
    width: 100%;
    height: ${TRACK_HEIGHT}px;
    border-radius: 50px;
  }
`;

const LoadedTrack = styled.div`
  background-color: #000000;
  position: absolute;
  top: 5px;
  left: 0;
  width: ${props => props.width}%;
  height: ${TRACK_HEIGHT}px;
    border-radius: 50px;
`;

const LoadingCover = styled.div`
  position: absolute;
  top: 6px;
  left: 75px;
  font-size: 12px;
  padding: 2px 4px;

  @media only screen and (min-width: 700px) {
    top: -20px;
    left: 107px;
  }
`;

const CuePointsMarker = styled.div`
  background-color: ${props => props.color};
  position: absolute;
  top: 5px;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
  height: ${TRACK_HEIGHT}px;
  border-radius: 50px;

  /* ${props => props.isIntersect && css`
    background-color: rgba(255, 224, 0, 0.8);
  `} */
`;

const CuePointsTimer = styled.div`
  display: inline-block;
  width: 60px;
  text-align: ${props => !!props.align ? props.align : 'left'};

  ${props => !!props.align && props.align === 'right' && css`
    font-weight: bold;
  `}
`;

const ClipList = styled.li`
  width: 100%;
  background-color: ${props => props.theme.colors.player};
  list-style: none;
`;

const ClipListItem = styled.div`
  display: flex;
  align-items: start;
  width: 100%;
  box-sizing: border-box;
  padding: 0 10px;
  font-size: 14px;

  div {
    width: calc(100% - 100px);
    padding: 8px;
    box-sizing: border-box;
  }

  div:last-child {
    text-align: right;
    width: 104px;
  }

  ${props => !!props.isIntersect && css`
    background-color: ${props.color};
    font-weight: bold;
  `}

  @media only screen and (min-width: 700px) {
    font-size: 20px;
    padding: 0 50px;

    div:last-child {
      text-align: right;
      width: 200px;
    }
  }
`;

class HTML5Player extends React.Component {
  player = null;
  loadTimerId = 0;
  playTimerId = 0;
  currentCueIds = [];

  constructor(props) {
    super(props);

    this.state = {
      playedSeconds: 0,
      duration: 0,
      playing: false,
      played: 0,
      loaded: 0,
      ready: false
    }

    this.player = React.createRef();
  }

  componentDidMount() {
    const { cuePoints } = this.props;
    const UNLOAD_EVENT = ua.os.name === 'iOS' ? 'pagehide' : 'beforeunload';
    window.addEventListener(UNLOAD_EVENT, this.onBeforeUnload);

    this.currentCueIds = cuePoints.map(item => -1);
  }

  componentDidUpdate(prevProps, prevState) {
    const { playing, hidden, songUrl, audioType } = this.props;

    if (prevProps.hidden !== hidden) {
      if(!!this.player.current) {
        this.player.current.seekTo(0, 'seconds');
      }
      this.setState({ playing: false });
    }

    if (!!prevProps.hidden && !hidden) {
      this.loadTimerId = setTimeout(() => {
        this.loadSong();
        clearTimeout(this.loadTimerId);
      }, 200);
    }

    if (this.state.playing !== playing) {
      this.playTimerId = setTimeout(() => {
        this.setState({ playing });
        clearTimeout(this.playTimerId);
      }, 300);
    }

    if (prevProps.songUrl !== songUrl && !!playing) {
      dataLayer.push({ event: 'CodeAudioPlayerEvent', eventAction: `${audioType}/play_song_progress`, eventLabel: `${prevProps.pageData.name}/${prevProps.currentSongData.name}/${prevState.playedSeconds}/${prevState.duration}` });
    }
  }

  componentWillUnmount() {
    this.setState({ playing: false });
  }

  onBeforeUnload = event => {
    const { playing, pageData, currentSongData, audioType } = this.props;
    const { playedSeconds, duration } = this.state;

    event.preventDefault();

    if(!!playing) {
      dataLayer.push({ event: 'CodeAudioPlayerEvent', eventAction: `${audioType}/play_song_progress`, eventLabel: `${pageData.name}/${currentSongData.name}/${playedSeconds}/${duration}` });
    }
  }

  onReady = () => {
    this.setState({ ready: true, error: false });
  };

  onDuration = () => {
    if (!!this.player) {
      this.setState({ duration: this.player.current.getDuration() });
    }
  };

  loadSong = () => {
    const { songUrl } = this.props;
    if(!!this.player && songUrl !== '') {
      const player = this.player.current.getInternalPlayer();
      player.load();
    }
  };

  goPlay = () => {
    this.setState(state => ({ playing: !state.playing }));
  };

  onProgress = ({ loaded, played, loadedSeconds, playedSeconds }) => {
    const { duration } = this.state;
    this.setState(state => ({ loaded, played, loadedSeconds, playedSeconds, ready: loadedSeconds > playedSeconds + (loadedSeconds < duration / 3 ? 30 : 5) || (!!duration && loadedSeconds === duration) }));
  };

  onEnded = () => {
    const { audioType, pageData, currentSongData } = this.props;
    const { duration } = this.state;
    this.setState(state => ({ playing: false, playedSeconds: 0, loaded: 0  }));
    this.onSeek(0);
    dataLayer.push({ event: 'CodeAudioPlayerEvent', eventAction: `${audioType}/play_song_completed`, eventLabel: `${pageData.name}/${currentSongData.name}` });
    dataLayer.push({ event: 'CodeAudioPlayerEvent', eventAction: `${audioType}/play_song_progress`, eventLabel: `${pageData.name}/${currentSongData.name}/${duration}/${duration}` });

  };

  onError = error => {
    console.log(error);
    this.setState({ error: true });
  };

  onSeek = played => {
    this.player.current.seekTo(played / 100, 'fraction');
  };

  renderCuePointersGuide = points => {
    const { audioType, pageData, currentSongData } = this.props;
    const { playedSeconds } = this.state;

    return points.map((item, index) => {
      const start = item.start.replace(/\s+/g, '');
      const end = item.end.replace(/\s+/g, '');
      const isIntersect = playedSeconds >= toTime(start) && playedSeconds <= toTime(end);

      if(!!isIntersect && this.currentCueIds[index] === -1) {
        dataLayer.push({ event: 'CodeAudioPlayerEvent', eventAction: `${audioType}/play_annotation`, eventLabel: `${pageData.name}/${currentSongData.name}/${toTime(start)}/${item.description}` });
        this.currentCueIds[index] = item.id;
      }
      
      if (!isIntersect && this.currentCueIds[index] !== -1) {
        this.currentCueIds[index] = -1;
      }

      return (
        <ClipList
          key={index}
          onClick={() => { 
            this.currentCueIds[index] = -1;
            this.player.current.seekTo(toTime(start), 'seconds');
            dataLayer.push({ event: 'CodeAudioPlayerEvent', eventAction: `${audioType}/click_annotation`, eventLabel: `${pageData.name}/${currentSongData.name}/${playedSeconds}/${item.description}` });
          }}
        >
          <ClipListItem
            color={colors[index]}
            isIntersect={isIntersect}
          >
          <div>{typeof item.description !== 'undefined' && item.description.split('\n').map(function(item, idx) {
                      return (
                          <span key={idx}>
                              {item}
                              <br/>
                          </span>
                      )
                  })}</div>
          <div>{start} - {end}</div>
          </ClipListItem>
        </ClipList>
      )
    });
  }

  renderCuePointersMarker = points => {
    const { duration, playedSeconds } = this.state;

    return points.map((item, index) => {
      const start = toTime(item.start);
      const end = toTime(item.end);
      const isIntersect = playedSeconds >= start && playedSeconds <= end;

      return <CuePointsMarker
              key={index}
              isIntersect={isIntersect}
              left={start / duration * 100} width={(end - start) / duration * 100}
              color={colors[index]}
            />
    });
  }

  render() {
    const { cuePoints, songUrl, className, hidden } = this.props;
    const { playing, played, loaded, playedSeconds, ready, duration } = this.state;

    return (
      <Wrapper className={className} count={cuePoints.length} hidden={hidden}>
        {
          !hidden && (
            <ReactPlayer
              ref={this.player}
              width={0}
              height={0}
              url={`${process.env.REACT_APP_S3_URL}/experience${mode !== 'production' ? '_uat' : ''}/${songUrl}`}
              playsinline
              config={{
                file: {
                  forceAudio: true,
                  preload: 'metadata'
                }
              }} 
            playing={playing}
            onReady={this.onReady}
            onDuration={this.onDuration}
            onProgress={this.onProgress}
            onEnded={this.onEnded}
            onError={this.onError}
          />
      )}
      <CuePointsToolWrapper>
        <CuePointsTool>
          <CuePointsTimer>{toHHMMSS(playedSeconds)}</CuePointsTimer>
          <CuePointsBar>
            <LoadedTrack width={loaded * 100} />
            {!!cuePoints && this.renderCuePointersMarker(cuePoints)}
            <Slider value={played * 100} onChange={this.onSeek} />
          </CuePointsBar>
          <CuePointsTimer align="right">{toHHMMSS(duration)}</CuePointsTimer>
        </CuePointsTool>
        {!!cuePoints && this.renderCuePointersGuide(cuePoints)}
        {!ready && <LoadingCover>載入中...</LoadingCover>}
      </CuePointsToolWrapper>
    </Wrapper>
    )
  }
}

export default HTML5Player;